import { DolfinDiagram } from '../diagram'
import DolfinFlowExecutor from '../flow-executor/base'
import { NodeError, NodeWarning } from '../types'
import { DolfinNode } from './base'
import { TypedNode } from './types'

/**
 * Filter node - evaluates conditions and stops execution if they fail.
 *
 * @see /docs/domain/nodes/filter.md for canonical documentation
 */
export class FilterNode extends DolfinNode<DolfinFlowExecutor, 'filter'> {
  async process(): Promise<boolean> {
    await this.loadUserFields(this.formData.filters)
    await this.loadMetricAndTargetValues(this.formData.filters)
    await this.loadWeightedTargetAchievement(
      this.formData.targets,
      this.formData.use_target_weights
    )

    return this.evaluateLogic(this.formData.filters)
  }

  async parse() {
    return this.node
  }

  static validate(
    node: TypedNode<'filter'>,
    diagram: DolfinDiagram
  ): {
    errors: NodeError[]
    warnings: NodeWarning[]
  } {
    const superValidation = super.validate(node, diagram)

    const hasFilters = node.data.formData?.filters
    superValidation.errors.push(...this.validateWeightedTargetAchievement(node, hasFilters))

    if (!hasFilters) {
      superValidation.errors.push({
        message: 'Filter node must have filters',
        node
      })
    }

    if (hasFilters) {
      if (!Object.keys(hasFilters).length) {
        superValidation.errors.push({
          message: 'Filter node must have filters',
          node
        })
      }

      // Make it recursive
      const hasValues = Object.values(hasFilters).some(
        v => v !== null && v !== undefined && v !== '' && Array.isArray(v) && v.length > 0
      )

      if (!hasValues) {
        superValidation.errors.push({
          message: 'Filter node must have values',
          node
        })
      }
    }

    return {
      errors: [...superValidation.errors],
      warnings: [...superValidation.warnings]
    }
  }
}
